import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { API_PREFIX } from "@athenaeum/protocol/sync";
import {
  get,
  type FileContent,
  type Tree,
  type TreeEntry,
} from "../../services/api";
import { CopyCommand, Empty, ErrorBox, Loading } from "../../components/Ui";
import { Markdown } from "../../components/Markdown";
import {
  RepoSyncSteps,
  RepositoryId,
  TextOnlyNotice,
  serverUrl,
} from "../../components/SyncGuide";
import {
  bytes,
  encodePath,
  fullDate,
  shortRev,
  timeAgo,
} from "../../shared/utils/format";
import { useAsync, useTitle } from "../../hooks";
import { useManifest, useRepo } from "./RepoLayout";
import { NotFound } from "../workspace/NotFound";
import { Icon } from "../../components/Icon";
import { PageFinder } from "../../components/PageFinder";
import { listThreads } from "../../services/conversations";
import type { Revision } from "../../shared/types/api";

const revQuery = (rev: string | null) =>
  rev ? `?rev=${encodeURIComponent(rev)}` : "";

function Breadcrumbs({
  base,
  path,
  kind,
  rev,
}: {
  base: string;
  path: string;
  kind: "tree" | "blob";
  rev: string | null;
}) {
  const parts = path ? path.split("/") : [];
  return (
    <nav className="crumbs" aria-label="Path">
      <Link to={`${base}${revQuery(rev)}`}>Wiki</Link>
      {parts.map((part, i) => {
        const to = parts.slice(0, i + 1).join("/");
        const last = i === parts.length - 1;
        return (
          <span key={to} className="row" style={{ gap: 4 }}>
            <span className="sep" aria-hidden="true">
              /
            </span>
            {last && kind === "blob" ? (
              <span aria-current="page">{part}</span>
            ) : (
              <Link to={`${base}/tree/${encodePath(to)}${revQuery(rev)}`}>
                {part}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function FileTable({
  entries,
  base,
  rev,
}: {
  entries: TreeEntry[];
  base: string;
  rev: string | null;
}) {
  return (
    <table className="files">
      <caption className="sr-only">Wiki folders and files</caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Size</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.path} className={entry.type}>
            <td className="name">
              <span className="file-icon">
                <Icon name={entry.type === "dir" ? "folder" : "file"} />
              </span>
              <Link
                to={`${base}/${entry.type === "dir" ? "tree" : "blob"}/${encodePath(entry.path)}${revQuery(rev)}`}
              >
                {entry.name}
                {entry.type === "dir" ? "/" : ""}
              </Link>
            </td>
            <td className="size">
              {entry.size !== undefined ? bytes(entry.size) : ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The repository's front page: the root folder, and the README under it. */
export function RepoHome() {
  const { repo, base } = useRepo();
  const [params] = useSearchParams();
  const rev = params.get("rev");
  const tree = useAsync(
    () => repo.head || rev ? get<Tree>(`/repositories/${repo.id}/tree${revQuery(rev)}`) : Promise.resolve(undefined),
    [repo.id, rev, repo.head],
  );
  const revision = rev ?? tree.data?.revision ?? null;
  const {
    index,
    revision: manifest,
    error: manifestError,
    reload: reloadManifest,
  } = useManifest(repo.id, revision);
  const readmeEntry = tree.data?.entries.find(
    (e) => e.type === "file" && /^readme(\.md)?$/i.test(e.name),
  );
  const frontPath =
    readmeEntry?.path ??
    manifest?.manifest?.find((entry) =>
      /^wiki\/(index|readme)\.md$/i.test(entry.path),
    )?.path;
  const readme = useAsync(
    () =>
      frontPath
        ? get<FileContent>(
            `/repositories/${repo.id}/file?path=${encodeURIComponent(frontPath)}${revision ? `&rev=${encodeURIComponent(revision)}` : ""}`,
          )
        : Promise.resolve(undefined),
    [repo.id, frontPath, revision],
  );
  const server = serverUrl();
  const canWrite =
    repo.role === "write" || repo.role === "admin" || repo.role === "owner";

  return (
    <div className="split">
      <div className="stack" style={{ minWidth: 0 }}>
        {!repo.head && !rev ? (
          <EmptyRepo repo={repo} server={server} canWrite={canWrite} />
        ) : tree.loading && !tree.data ? (
          <Loading />
        ) : tree.error ? (
          <ErrorBox error={tree.error} retry={tree.reload} />
        ) : (
          <>
            {rev && (
              <div className="alert warn">
                Showing revision <code>{shortRev(rev)}</code>.{" "}
                <Link to={base}>Back to the latest</Link>.
              </div>
            )}
            {manifest?.manifest && (
              <PageFinder
                paths={manifest.manifest.map((entry) => entry.path)}
                base={base}
                rev={rev}
              />
            )}
            <div className="card">
              <div className="card-head">
                <span>Files</span>
                <span className="muted right" style={{ fontWeight: 400 }}>
                  <Link to={`${base}/commits`}>
                    {shortRev(revision)} · updated {timeAgo(repo.updatedAt)}
                  </Link>
                </span>
              </div>
              {tree.data && tree.data.entries.length > 0 ? (
                <FileTable entries={tree.data.entries} base={base} rev={rev} />
              ) : (
                <Empty title="This wiki has no files" />
              )}
            </div>
            {manifestError && (
              <ErrorBox error={manifestError} retry={reloadManifest} />
            )}
            {readme.error && (
              <ErrorBox error={readme.error} retry={readme.reload} />
            )}
            {frontPath && readme.loading && !readme.data && <Loading />}
            {readme.data && frontPath && (
              <div className="card document-panel">
                <div className="card-head">
                  <Icon name="file" />
                  <Link
                    to={`${base}/blob/${encodePath(frontPath)}${revQuery(rev)}`}
                  >
                    {frontPath}
                  </Link>
                </div>
                <div className="card-pad">
                  <Markdown
                    text={readme.data.content}
                    base={base}
                    rev={rev}
                    index={index}
                    filePath={frontPath}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <aside className="stack">
        <div className="repo-about">
          <h3>About</h3>
          <p className={repo.description ? "" : "muted"}>
            {repo.description || "No description."}
          </p>
          {!!repo.topics?.length && (
            <div className="row topic-list">
              {repo.topics.map((topic) => (
                <Link
                  className="badge"
                  to={`/explore?topic=${encodeURIComponent(topic)}`}
                  key={topic}
                >
                  {topic}
                </Link>
              ))}
            </div>
          )}
          <div
            className="repo-meta"
            style={{ flexDirection: "column", gap: 4 }}
          >
            <span>
              {repo.fileCount} {repo.fileCount === 1 ? "file" : "files"} ·{" "}
              {bytes(repo.sizeBytes)}
            </span>
            {manifest?.manifest && (
              <span>
                {
                  manifest.manifest.filter((entry) => /\.md$/i.test(entry.path))
                    .length
                }{" "}
                Markdown pages in this revision
              </span>
            )}
            <Link to={`${base}/forks`}>
              {repo.forks} {repo.forks === 1 ? "fork" : "forks"}
            </Link>
            {repo.starCount !== undefined && (
              <span>{repo.starCount} stars</span>
            )}
            <span>Updated {timeAgo(repo.updatedAt)}</span>
            <span title={fullDate(repo.createdAt)}>
              Created {timeAgo(repo.createdAt)}
            </span>
          </div>
        </div>
        <div className="card card-pad stack repo-local-guide">
          <h3>Use with Athenaeum</h3>
          <RepositoryId id={repo.id} />
          {repo.head && (
            <RepoSyncSteps repo={repo} server={server} canWrite={canWrite} />
          )}
        </div>
      </aside>
    </div>
  );
}

function EmptyRepo({
  repo,
  server,
  canWrite,
}: {
  repo: { fullName: string };
  server: string;
  canWrite: boolean;
}) {
  return (
    <div className="card card-pad stack">
      <h2>This wiki is empty</h2>
      {canWrite ? (
        <>
          <p className="muted">
            Nothing has been pushed yet. In the folder that holds your wiki,
            connect it and push:
          </p>
          <CopyCommand
            label="Sign in"
            text={`athenaeum library login --server ${server}`}
          />
          <CopyCommand
            label="Link"
            text={`athenaeum sync link --server ${server} --remote ${repo.fullName}`}
          />
          <CopyCommand
            label="Push"
            text={'athenaeum sync push -m "First push"'}
          />
          <TextOnlyNotice />
        </>
      ) : (
        <p className="muted">Nothing has been pushed to it yet.</p>
      )}
    </div>
  );
}

export function RepoTree() {
  const { repo, base } = useRepo();
  const path = useParams()["*"] ?? "";
  useTitle(`${path || "Pages"} · ${repo.fullName}`);
  const [params] = useSearchParams();
  const rev = params.get("rev");
  const tree = useAsync(
    () =>
      repo.head || rev ? get<Tree>(
        `/repositories/${repo.id}/tree?path=${encodeURIComponent(path)}${rev ? `&rev=${encodeURIComponent(rev)}` : ""}`,
      ) : Promise.resolve(undefined),
    [repo.id, path, rev, repo.head],
  );
  if (!repo.head && !rev) return <Empty title="This wiki has no files"><p>Sync your wiki pages from Athenaeum to see them here.</p><Link className="btn" to={base}>Set up this wiki</Link></Empty>;
  if (tree.error?.status === 404) return <NotFound what="folder" />;
  return (
    <div className="stack">
      <Breadcrumbs base={base} path={path} kind="tree" rev={rev} />
      {tree.loading && !tree.data ? (
        <Loading />
      ) : tree.error ? (
        <ErrorBox error={tree.error} retry={tree.reload} />
      ) : (
        <div className="card">
          {tree.data && tree.data.entries.length > 0 ? (
            <FileTable entries={tree.data.entries} base={base} rev={rev} />
          ) : (
            <Empty title="This folder is empty" />
          )}
        </div>
      )}
    </div>
  );
}

const MARKDOWN = /\.md$/i;

export function RepoBlob() {
  const { repo, base } = useRepo();
  const path = useParams()["*"] ?? "";
  useTitle(`${path.split("/").pop() ?? "Page"} · ${repo.fullName}`);
  const [params] = useSearchParams();
  const rev = params.get("rev");
  const file = useAsync(
    () =>
      get<FileContent>(
        `/repositories/${repo.id}/file?path=${encodeURIComponent(path)}${rev ? `&rev=${encodeURIComponent(rev)}` : ""}`,
      ),
    [repo.id, path, rev, repo.head],
  );
  const {
    index,
    revision,
    error: manifestError,
    reload: reloadManifest,
  } = useManifest(repo.id, file.data?.revision);
  const [mode, setMode] = useState<"preview" | "source">("preview");
  const issues = useAsync(
    () =>
      listThreads(repo.id, "issue", {
        state: "open",
        pagePath: path,
        perPage: 1,
      }),
    [repo.id, path],
  );

  if (file.error?.status === 404) return <NotFound what="file" />;
  const data = file.data;
  const isMarkdown = MARKDOWN.test(path);
  const rawRevision = data?.revision ?? rev;
  const rawHref = `${API_PREFIX}/repositories/${repo.id}/raw?path=${encodeURIComponent(path)}${rawRevision ? `&rev=${encodeURIComponent(rawRevision)}` : ""}`;

  return (
    <div className="stack">
      <Breadcrumbs base={base} path={path} kind="blob" rev={rev} />
      {rev && (
        <div className="alert warn">
          Browsing a historical revision.{" "}
          <Link to={`${base}/blob/${encodePath(path)}`}>
            View the current page
          </Link>
          .
        </div>
      )}
      <div className="wiki-reader document-reader">
        <div className="stack">
          {manifestError && (
            <ErrorBox error={manifestError} retry={reloadManifest} />
          )}
          {file.loading && !data ? (
            <Loading />
          ) : file.error ? (
            <ErrorBox error={file.error} retry={file.reload} />
          ) : (
            data && (
              <div className="card document-panel">
                <div className="card-head">
                  <Icon name="file" />
                  <span className="mono">{path.split("/").pop()}</span>
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {bytes(data.size)} · {shortRev(data.revision)}
                  </span>
                  <div className="right row" style={{ gap: 8 }}>
                    {isMarkdown && (
                      <div
                        className="view-toggle"
                        role="group"
                        aria-label="View"
                      >
                        <button
                          aria-pressed={mode === "preview"}
                          onClick={() => setMode("preview")}
                        >
                          Preview
                        </button>
                        <button
                          aria-pressed={mode === "source"}
                          onClick={() => setMode("source")}
                        >
                          Source
                        </button>
                      </div>
                    )}
                    <a
                      className="btn sm"
                      href={rawHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Raw
                    </a>
                    <Link
                      className="btn sm"
                      to={`${base}/commit/${data.revision}`}
                    >
                      Revision details
                    </Link>
                  </div>
                </div>
                {isMarkdown && mode === "preview" ? (
                  <div className="card-pad">
                    <Markdown
                      text={data.content}
                      base={base}
                      rev={rev}
                      index={index}
                      filePath={data.path}
                    />
                  </div>
                ) : (
                  <Source text={data.content} />
                )}
              </div>
            )
          )}
          {data && isMarkdown && (
            <p className="hint page-issues">
              <Icon name="issue" />
              {issues.data && issues.data.total > 0 ? (
                <Link
                  to={`${base}/issues?pagePath=${encodeURIComponent(path)}`}
                >
                  {issues.data.total} open{" "}
                  {issues.data.total === 1 ? "issue" : "issues"} about this page
                </Link>
              ) : (
                <span>No open issues about this page.</span>
              )}
              {" · "}
              <Link to={`${base}/issues/new?page=${encodeURIComponent(path)}`}>
                Report a problem with this page
              </Link>
            </p>
          )}
          {data && (
            <div className="page-context">
              <p className="hint">
                Snapshot{" "}
                <Link to={`${base}/commit/${data.revision}`}>
                  {shortRev(data.revision)}
                </Link>
                {revision && (
                  <>
                    {" "}
                    · synced by{" "}
                    <Link to={`/user/${revision.author}`}>
                      {revision.author}
                    </Link>{" "}
                    ·{" "}
                    <time dateTime={revision.createdAt}>
                      {fullDate(revision.createdAt)}
                    </time>
                  </>
                )}
              </p>
              {["owner", "admin", "write"].includes(repo.role) && (
                <p className="hint">
                  To change this page, edit it in your local Athenaeum wiki,
                  review sync status, and push.{" "}
                  <Link to="/explore/guide">Sync guide</Link>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Source({ text }: { text: string }) {
  const lines = text.split("\n");
  if (lines[lines.length - 1] === "") lines.pop();
  return (
    <pre
      className="code-view"
      tabIndex={0}
      aria-label="File contents"
      style={{ padding: "10px 0" }}
    >
      {lines.map((line, i) => (
        <span className="line" key={i}>
          <span className="ln" aria-hidden="true">
            {i + 1}
          </span>
          {line}
        </span>
      ))}
    </pre>
  );
}
